import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { getEvent, registerGuest } from '../api/confetti'
import { isNotFound } from '../api/client'
import { SessionContext, type SessionGuest } from './context'
import { clearSession, readGuest, writeGuest } from './storage'
import { useLiveEvent } from './useLiveEvent'
import { BrokenLink } from '../components/BrokenLink'
import { useDocumentTitle } from '../lib/title'
import { LoadingScreen } from '../components/LoadingScreen'

export function SessionProvider({ children }: { children: ReactNode }) {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [guest, setGuest] = useState<SessionGuest | null>(() => readGuest(slug))

  const event = useQuery({
    queryKey: ['event', slug],
    queryFn: ({ signal }) => getEvent(slug, signal),
    enabled: slug !== '',
    retry: (count, error) => !isNotFound(error) && count < 2,
  })

  const saveGuest = useCallback(
    async (displayName: string, useOwnerAccount = false) => {
      const saved = await registerGuest(slug, {
        guestId: guest?.guestId,
        displayName,
        useOwnerAccount,
      })

      const next = { guestId: saved.guestId, displayName: saved.displayName }

      writeGuest(slug, next)
      setGuest(next)

      // De naam staat bij elke foto, en het aantal gasten in de kop. Beide
      // zijn nu verouderd.
      await queryClient.invalidateQueries({ queryKey: ['event', slug] })
      await queryClient.invalidateQueries({ queryKey: ['media', slug] })
      await queryClient.invalidateQueries({ queryKey: ['guest', slug] })

      return next
    },
    [guest?.guestId, queryClient, slug],
  )

  const signOut = useCallback(() => {
    clearSession(slug)
    setGuest(null)
    queryClient.clear()
    void navigate('/', { replace: true })
  }, [navigate, queryClient, slug])

  // Zolang deze schil openstaat luisteren we mee: nieuwe foto's van andere
  // gasten komen er dan vanzelf bij.
  useLiveEvent(slug)

  // De naam van het feest in het tabblad, en niets erachter: dit scherm is van
  // het bruidspaar. Zolang het nog laadt of de link niet klopt staat er onze
  // eigen naam, want dan valt er niets anders te zeggen.
  useDocumentTitle(event.data?.coupleNames ?? 'Confetti')

  const value = useMemo(
    () => (event.data ? { slug, event: event.data, guest, saveGuest, signOut } : null),
    [event.data, guest, saveGuest, signOut, slug],
  )

  if (isNotFound(event.error)) {
    return <BrokenLink />
  }

  if (event.isError) {
    return (
      <BrokenLink
        title="Even niet bereikbaar"
        detail="We konden het feest niet ophalen. Controleer je verbinding en probeer het zo nog eens."
      />
    )
  }

  if (!value) {
    return <LoadingScreen />
  }

  return (
    <SessionContext.Provider value={value}>
      {/*
        De accentkleur van dit feest voor de rest van de boom. Eén variabele:
        alles wat accent gebruikt — knoppen, pillen, de voortgangsbalk — volgt
        vanzelf, en de rest van het palet blijft staan.

        De gekozen kleur komt binnen als `--color-accent-raw` en niet als
        `--color-accent` zelf. De stylesheet maakt er het echte token van, en
        kan er in donkere modus nog iets mee doen — zie `tokens.css`. Zou de
        kleur hier rechtstreeks in `--color-accent` staan, dan won die als
        inline stijl van elke regel en was daar geen ruimte meer voor.
      */}
      <div
        className="contents"
        data-accent=""
        style={{ '--color-accent-raw': value.event.accentColor } as CSSProperties}
      >
        {children}
      </div>
    </SessionContext.Provider>
  )
}
